import {
  Button,
  Checkbox,
  Combobox,
  Dropdown,
  Field,
  Input,
  makeStyles,
  Option,
  tokens,
} from "@fluentui/react-components";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { TimePicker } from "@fluentui/react-timepicker-compat";
import {
  CalendarLtr20Regular,
  Clock20Regular,
} from "@fluentui/react-icons";
import { useCallback, useMemo } from "react";

import { useDocumentTypes } from "@/features/documents/useDocumentTypes";
import { useFilterStore } from "@/store/filterStore";

const DATE_FMT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "numeric",
  year: "numeric",
});

function formatPickerDate(d?: Date): string {
  return d ? DATE_FMT.format(d) : "";
}

function parsePickerDate(s: string): Date | null {
  if (!s.trim()) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

interface DateTimeParts {
  date: Date | null;
  time: string;
}

function utcToLocalParts(value: string | null): DateTimeParts {
  if (!value) return { date: null, time: "" };
  let utc = value;
  if (!utc.includes("T")) utc += "T00:00:00Z";
  else if (!utc.endsWith("Z") && !/[+-]\d{2}:?\d{2}$/.test(utc)) utc += "Z";
  const d = new Date(utc);
  if (isNaN(d.getTime())) return { date: null, time: "" };
  const dateOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const p = (n: number) => String(n).padStart(2, "0");
  return { date: dateOnly, time: `${p(d.getHours())}:${p(d.getMinutes())}` };
}

function localPartsToUtc(date: Date | null, time: string): string | null {
  if (!date) return null;
  const [h, m] = time ? time.split(":").map(Number) : [0, 0];
  const local = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    h || 0,
    m || 0,
  );
  if (isNaN(local.getTime())) return null;
  return local.toISOString().replace(/\.\d{3}Z$/, "Z");
}

function timeStringToDate(time: string): Date | null {
  if (!time) return null;
  const parts = time.split(":").map(Number);
  const h = parts[0];
  const m = parts[1];
  if (h === undefined || m === undefined || Number.isNaN(h) || Number.isNaN(m)) {
    return null;
  }
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d;
}

function dateToTimeString(d: Date | null | undefined): string {
  if (!d) return "";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
}

const useStyles = makeStyles({
  container: {
    padding: "14px 16px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    background: tokens.colorNeutralBackground1,
    display: "grid",
    columnGap: "18px",
    rowGap: "10px",
    alignItems: "end",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    "@media (max-width: 900px)": {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
    "@media (max-width: 520px)": {
      gridTemplateColumns: "1fr",
    },
  },
  resetCell: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "flex-end",
    minHeight: "32px",
  },
  dateTimeRow: {
    display: "flex",
    gap: "6px",
    minWidth: 0,
  },
  datePart: {
    width: "9.5em",
    flexShrink: 0,
  },
  timePart: {
    flex: 1,
    minWidth: 0,
  },
  docSecondaryRow: {
    gridColumn: "1 / -1",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "16px",
    paddingTop: "4px",
    borderTop: `1px dashed ${tokens.colorNeutralStroke3}`,
    marginTop: "4px",
  },
  docSecondaryField: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    minWidth: "180px",
  },
  docSecondaryLabel: {
    fontSize: "12px",
    color: tokens.colorNeutralForeground3,
  },
  iconLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
  },
});

export function FilterBar() {
  const styles = useStyles();
  const subject = useFilterStore((s) => s.subject);
  const from = useFilterStore((s) => s.from);
  const documentType = useFilterStore((s) => s.documentType);
  const listTab = useFilterStore((s) => s.listTab);
  const dateFrom = useFilterStore((s) => s.dateFrom);
  const dateTo = useFilterStore((s) => s.dateTo);
  const sort = useFilterStore((s) => s.sort);
  const tradeKey = useFilterStore((s) => s.tradeKey);
  const correctedOnly = useFilterStore((s) => s.correctedOnly);
  const mode = useFilterStore((s) => s.mode);
  const setField = useFilterStore((s) => s.set);
  const reset = useFilterStore((s) => s.reset);
  const { data: documentTypes } = useDocumentTypes();

  // Filter sidebar only offers review-relevant types (archive-only docs
  // are surfaced separately on the email drawer), sorted alphabetically.
  const reviewDocumentTypes = useMemo(
    () =>
      (documentTypes ?? [])
        .filter((dt) => !dt.archiveOnly)
        .slice()
        .sort((a, b) => a.label.localeCompare(b.label)),
    [documentTypes],
  );

  const fromParts = utcToLocalParts(dateFrom);
  const toParts = utcToLocalParts(dateTo);
  const fromTimeDate = useMemo(
    () => timeStringToDate(fromParts.time),
    [fromParts.time],
  );
  const toTimeDate = useMemo(
    () => timeStringToDate(toParts.time),
    [toParts.time],
  );

  const onSubject = useCallback(
    (_: unknown, data: { value: string }) => setField("subject", data.value),
    [setField],
  );
  const onFrom = useCallback(
    (_: unknown, data: { value: string }) => setField("from", data.value),
    [setField],
  );
  const onTradeKey = useCallback(
    (_: unknown, data: { value: string }) => setField("tradeKey", data.value),
    [setField],
  );

  const hasAny =
    Boolean(subject) ||
    Boolean(from) ||
    Boolean(documentType) ||
    Boolean(dateFrom) ||
    Boolean(dateTo) ||
    Boolean(tradeKey) ||
    sort !== "newest" ||
    correctedOnly ||
    listTab !== "needsReview" ||
    mode !== "emails";

  return (
    <div className={styles.container}>
      <Field label="Subject">
        <Input value={subject} onChange={onSubject} placeholder="Contains text…" />
      </Field>
      <Field label="From">
        <Input value={from} onChange={onFrom} placeholder="Contains text…" />
      </Field>
      <Field label="Document type">
        <Combobox
          placeholder="Any type"
          value={
            reviewDocumentTypes.find((dt) => dt.key === documentType)?.label ??
            ""
          }
          selectedOptions={documentType ? [documentType] : []}
          onOptionSelect={(_, data) =>
            setField(
              "documentType",
              data.optionValue === "" ? null : (data.optionValue ?? null),
            )
          }
        >
          <Option value="">Any type</Option>
          {reviewDocumentTypes.map((dt) => (
            <Option key={dt.key} value={dt.key}>
              {dt.label}
            </Option>
          ))}
        </Combobox>
      </Field>
      <Field label="Trade key">
        <Input
          value={tradeKey}
          onChange={onTradeKey}
          placeholder="e.g. S259347.1.1"
          title="Format: S259347.1.1 (sale) or B259347.1.1 (purchase)"
        />
      </Field>
      <Field
        label={
          <span className={styles.iconLabel}>
            <CalendarLtr20Regular aria-hidden="true" />
            Email arrived after
          </span>
        }
      >
        <div className={styles.dateTimeRow}>
          <DatePicker
            value={fromParts.date}
            onSelectDate={(d) =>
              setField("dateFrom", localPartsToUtc(d ?? null, fromParts.time))
            }
            formatDate={formatPickerDate}
            parseDateFromString={parsePickerDate}
            maxDate={toParts.date ?? undefined}
            placeholder="Any"
            allowTextInput
            className={styles.datePart}
          />
          <TimePicker
            freeform
            hourCycle="h23"
            increment={15}
            selectedTime={fromTimeDate}
            expandIcon={<Clock20Regular aria-hidden="true" />}
            onTimeChange={(_, data) =>
              setField(
                "dateFrom",
                localPartsToUtc(
                  fromParts.date,
                  dateToTimeString(data.selectedTime),
                ),
              )
            }
            disabled={!fromParts.date}
            className={styles.timePart}
          />
        </div>
      </Field>
      <Field
        label={
          <span className={styles.iconLabel}>
            <CalendarLtr20Regular aria-hidden="true" />
            Email arrived before
          </span>
        }
      >
        <div className={styles.dateTimeRow}>
          <DatePicker
            value={toParts.date}
            onSelectDate={(d) =>
              setField("dateTo", localPartsToUtc(d ?? null, toParts.time))
            }
            formatDate={formatPickerDate}
            parseDateFromString={parsePickerDate}
            minDate={fromParts.date ?? undefined}
            placeholder="Any"
            allowTextInput
            className={styles.datePart}
          />
          <TimePicker
            freeform
            hourCycle="h23"
            increment={15}
            selectedTime={toTimeDate}
            expandIcon={<Clock20Regular aria-hidden="true" />}
            onTimeChange={(_, data) =>
              setField(
                "dateTo",
                localPartsToUtc(
                  toParts.date,
                  dateToTimeString(data.selectedTime),
                ),
              )
            }
            disabled={!toParts.date}
            className={styles.timePart}
          />
        </div>
      </Field>
      <Field label="Sort">
        <Dropdown
          value={sort === "oldest" ? "Oldest first" : "Newest first"}
          selectedOptions={[sort]}
          onOptionSelect={(_, data) =>
            setField("sort", data.optionValue === "oldest" ? "oldest" : "newest")
          }
        >
          <Option value="newest">Newest first</Option>
          <Option value="oldest">Oldest first</Option>
        </Dropdown>
      </Field>
      <div className={styles.resetCell}>
        <Button
          appearance="subtle"
          onClick={() => reset()}
          disabled={!hasAny}
        >
          Reset filters
        </Button>
      </div>
      {mode === "documents" ? (
        <div className={styles.docSecondaryRow}>
          <Checkbox
            label="Only manually corrected"
            checked={correctedOnly}
            onChange={(_, d) => setField("correctedOnly", Boolean(d.checked))}
          />
        </div>
      ) : null}
    </div>
  );
}
